import _ from 'lodash'

import { isValidConfigurationForTMO } from 'core/service/basic-plan/validation'

/**
 * @param dataWrapper {ReturnType<import('core/data/data-wrapper')._DB>}
 * @param riderRepository {ReturnType<import('core/data-model/rider/repository').createRiderRepository>}
 */
export function createBasicPlanRepository(dataWrapper, riderRepository) {
  const { list, get } = dataWrapper
  const DOCUMENT_KEY = 'product'

  const _getViewConfiguration = (get) => get('view-configuration')

  const getViewConfiguration = () => _getViewConfiguration(get)

  const _listBasicPlans = (list) => list(DOCUMENT_KEY)

  const listBasicPlans = () => _listBasicPlans(list)

  const _listActiveBasicPlans = (listBasicPlans) => Promise.resolve(listBasicPlans())

  const listActiveBasicPlans = () => _listActiveBasicPlans(listBasicPlans)

  const getRawBasicPlan = async (code) => {
    const basicPlan = await get(`${DOCUMENT_KEY}_${code}`)

    return basicPlan
  }

  const override = (object, overrides) => {
    if (overrides == null || object == null) {
      return object
    } else {
      return { ...object, ...overrides }
    }
  }

  const _overrideByAgentCode = (basicPlanByType, distributorGroup = '', agentCode = '') => {
    if (
      isValidConfigurationForTMO(
        basicPlanByType.code,
        basicPlanByType.userOverridesEffectiveDate ? basicPlanByType.userOverridesEffectiveDate : '01-01-2000 00:00:00',
        basicPlanByType.userOverridesExpiryDate ? basicPlanByType.userOverridesExpiryDate : '01-01-2999 00:00:00'
      ) &&
      'userOverridesList' in basicPlanByType &&
      'userOverrides' in basicPlanByType &&
      basicPlanByType.userOverridesList.includes(`${distributorGroup}-${agentCode}`)
    ) {
      return override(basicPlanByType, basicPlanByType.userOverrides)
    } else {
      return basicPlanByType
    }
  }

  const _overrideRiderByUserType = (rawRider, distributorGroup = '') => {
    if (distributorGroup === 'BANC' && 'bancOverrides' in rawRider) {
      return override(rawRider, rawRider.bancOverrides)
    }
    if (distributorGroup === 'AGENT' && 'agentOverrides' in rawRider) {
      return override(rawRider, rawRider.agentOverrides)
    }
    return rawRider
  }

  const _overrideByUserType = (rawBasicPlan, distributorGroup = '') => {
    if (distributorGroup === 'BANC' && 'bancOverrides' in rawBasicPlan) {
      return override(rawBasicPlan, rawBasicPlan.bancOverrides)
    }
    if (distributorGroup === 'AGENT' && 'agentOverrides' in rawBasicPlan) {
      return override(rawBasicPlan, rawBasicPlan.agentOverrides)
    }
    return rawBasicPlan
  }

  const _overrideRiderProps = (basicPlan, rider, code) => {
    const riderOverrideProps = _.get(basicPlan, `ridersOverrides.${code}`, null)
    if (riderOverrideProps && rider.code === 'MHP') {
      const riderPackage = rider.packageRiders.find((p) =>
        Object.values(_.get(p, 'codes.Thailand')).includes(riderOverrideProps.options[0].values[0].planCode)
      )
      riderOverrideProps.defaultSumAssured = riderPackage ? riderPackage.defaultSumAssured : rider.defaultSumAssured
    }
    return riderOverrideProps
  }

  const _getBasicPlan = async (getRawBasicPlan, getRider, code, distributorGroup = '', agentCode = '') => {
    const rawBasicPlan = await getRawBasicPlan(code)
    const basicPlanByType = _overrideByUserType(rawBasicPlan, distributorGroup)
    const basicPlan = _overrideByAgentCode(basicPlanByType, distributorGroup, agentCode)
    basicPlan.rules = basicPlan.rules || []
    basicPlan.riders = basicPlan.riders || []
    basicPlan.readOnlyFields = basicPlan.readOnlyFields || {}
    const riders = await Promise.all(
      basicPlan.riders.map((riderCode) => {
        return new Promise(async (resolve, reject) => {
          const code = riderCode.replace('rider_', '')
          const rawRider = await getRider(code)
          const rider = _overrideRiderByUserType(rawRider, distributorGroup)
          const riderOverrideProps = _overrideRiderProps(basicPlan, rider, code)
          resolve(override(rider, riderOverrideProps))
        })
      })
    )

    let activeRiders = []
    for (var rider of riders) {
      if (rider.effectiveDate === undefined && rider.expiryDate === undefined) {
        activeRiders.push(rider)
      } else if (isValidConfigurationForTMO(rider.code, rider.effectiveDate, rider.expiryDate)) {
        activeRiders.push(rider)
      }
    }
    // $FlowFixMe
    const result = { ...basicPlan, riders: activeRiders }
    return result
  }

  const getBasicPlan = (code, distributorGroup, agentCode) =>
    _getBasicPlan(getRawBasicPlan, riderRepository.getRider, code, distributorGroup, agentCode)

  const _getBasicPlanRuleByType = (getBasicPlan) => async (code, ruleType) => {
    const basicPlan = await getBasicPlan(code)
    const rule = basicPlan.rules.find(({ type }) => type === ruleType)

    if (rule != null && rule.type === ruleType) {
      return Promise.resolve(rule)
    } else {
      return Promise.reject(`Rule ${ruleType} does not exist`)
    }
  }

  const getBasicPlanRuleByType = _getBasicPlanRuleByType(getBasicPlan)

  const getProductDefaults = (code) => getRawBasicPlan(code).then(({ defaults }) => defaults)

  return {
    _getViewConfiguration,
    getViewConfiguration,
    _listBasicPlans,
    listBasicPlans,
    _listActiveBasicPlans,
    listActiveBasicPlans,
    _overrideRiderByUserType,
    _getBasicPlan,
    getBasicPlan,
    _getBasicPlanRuleByType,
    getBasicPlanRuleByType,
    getProductDefaults,
  }
}
